// @ts-check

import { registerPlugin } from '@mailonline/dmg-ads-core/src/plugin-registry'
import { leadingAndTrailingThrottle } from "@mailonline/dmg-ads-core/src/ads-utils/FunctionUtils";
import { Disposable } from '@mailonline/dmg-ads-core/src/ads-utils/EventUtils'
import { objectFilter } from '@mailonline/dmg-ads-core/src/ads-utils/MessageBus'
import { repeatUntil } from '@mailonline/dmg-ads-core/src/ads-utils/Async'


/**
 * @import {Context} from '../plugin-registry'
 * @import {Logger} from '../ads-utils/logger'
 */
registerPlugin('peekaboo', {
  ccpa: { enabledIfDissent: true },
  gdpr: { enabledIfDissent: true },
  isAdsFreeSupported: true,
  run
})

/**  @param {Context} context */
async function run({ broker, doc, getConfig, log }) {
  log('run')

  const killPeekaboo = getConfig('peekaboo.kill', true)
  const timeout = getConfig('peekaboo.timeout', 3000)
  const scrollThrottleMS = getConfig('peekaboo.scrollThrottleMS', 150)
  const config = getConfig('peekaboo.monitor_', /** @type {PeekabooMonitorConfig} */({
    outbrain_infinite_feed_sticky_banner: {
      recreateOnShow: true,
      pauseOnOverlay: true,
      conditions: {
        pluginEnabled: 'outbrain'
      },
      triggers: ".metro-infinite-feed-container",
      position: 'sticky_banner',
      offset: 1200,
      timeout: 1500
    },
    outbrain_sticky_banner: {
      recreateOnShow: true,
      pauseOnOverlay: true,
      conditions: {
        pluginEnabled: 'outbrain'
      },
      triggers: ".outbrain-feed, ad-slot[pos='chumbox']",
      position: 'sticky_banner',
      offset: 1200,
      timeout: 1500
    },
    outbrain_sticky_banner_top: {
      recreateOnShow: true,
      pauseOnOverlay: true,
      conditions: {
        pluginEnabled: 'outbrain'
      },
      triggers: ".outbrain-feed, ad-slot[pos='chumbox']",
      position: 'sticky_banner_top',
      offset: 1200,
      timeout: 1500
    },
    outbrain_ldr_top: {
      recreateOnShow: true,
      pauseOnOverlay: true,
      conditions: {
        pluginEnabled: 'outbrain'
      },
      triggers: ".outbrain-feed, ad-slot[pos='chumbox']",
      position: 'ldr_top',
      offset: 1200,
      timeout: 1500
    },
    outbrain_infinite_feed_ldr_top: {
      recreateOnShow: true,
      pauseOnOverlay: true,
      conditions: {
        pluginEnabled: 'outbrain'
      },
      triggers: ".metro-infinite-feed-container",
      position: 'ldr_top',
      offset: 1200,
      timeout: 1500
    },
  }))

  /** @type {Record<string, { check(): void, disposable: Disposable, paused: boolean }>} */
  const checks = {}
  /** @type {Set<string>} */
  const executedChecks = new Set()

  if (killPeekaboo) {
    broker.on('element visibile over outbrain', (key) => {
      const data = { key, hasCheck: key in checks, paused: !!checks[key]?.paused }
      if (!executedChecks.has(key)) {
        sendToBidmax({ ...data, type: 'kill before check' })
      }
      sendToBidmax({ ...data, type: 'kill' })
      log('killed', key)
      hidePos(config[key]?.position, log)
      checks[key]?.disposable.dispose()
      delete checks[key]
    })
  }

  const callbackfn = async ([key, value]) => {
    const _log = log.extend(key)
    const { pluginEnabled } = value.conditions

    if (pluginEnabled && !getConfig('plugins.enabled', /** @type {string[]} */([])).includes(pluginEnabled)) {
      _log(`plugin ${pluginEnabled} disabled, bailing`)
      return
    }

    checks[key] = {
      check: () => { },
      disposable: new Disposable(),
      paused: false
    }

    if (value.pauseOnOverlay) {
      checks[key].disposable.onBrokerEvent(broker, 'fullScreenOverlayStarted', () => {
        _log('fullScreenOverlayStarted')
        checks[key].paused = true
      })

      checks[key].disposable.onBrokerEvent(broker, 'fullScreenOverlayEnded', () => {
        _log('fullScreenOverlayEnded')
        checks[key].paused = false
      })
    }

    /** @type {Promise<unknown>} */

    try {
      await Promise.all([
        waitForSlotDefined(_log, value.position),
        Promise.race([waitForTrigger(_log, value.triggers)])
      ])
    } catch (error) {
      _log.error(error)
      return
    }

    _log('running for', key)
    const selector = document.querySelector(value.triggers)
    _log(selector)
    if (!selector) {
      _log(`Could not find selector ${value.triggers}`)
      return
    }

    const hide = () => {
      // we want this bail to be inside the if because otherwise it will trigger show on every scroll if you are past it
      if (!showing) return
      _log('hide offset triggered')
      clearTimeout(timeoutId)
      disposable?.dispose()
      disposable = new Disposable()
      const dis = hidePos(value.position, _log)
      disposable?.add(() => dis.dispose())
      showing = false
    }

    /** @type {Disposable|undefined} */
    let disposable
    let showing = true
    /** @type {NodeJS.Timeout | undefined} */
    let timeoutId

    checks[key].check = () => {
      if (!checks[key]) return
      if (checks[key].paused) return _log('paused')
      executedChecks.add(key)
      // We want this here because CLS....
      const clientRect = selector.getBoundingClientRect()
      const triggerOffset = clientRect.top - value.offset
      const bottomOffset = clientRect.bottom + value.offset

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const viewportHeight = window.innerHeight
      log({ scrollTop, viewportHeight, triggerOffset })
      // GetBoundingClientRect is relative to viewport, this is two conditions that we care about, aka we intersect the viewport (or are above/below it)
      // The only case where we hide is when the bottom of the rect is >= 0 (means its in viewport) and the top is < viewportHeight
      if (triggerOffset <= viewportHeight && bottomOffset >= 0) {
        hide()
      } else {
        if (showing) return
        showing = true
        timeoutId = setTimeout(() => {
          _log('trigger show')
          disposable?.dispose?.()
          showPos(value.position)
        }, value.timeout ?? timeout)
      }
    }

    checks[key].check()
  }
  Object.entries(config).forEach(callbackfn)

  window.addEventListener(
    'scroll',
    leadingAndTrailingThrottle(
      () => Object.values(checks).forEach(({ check }) => check()),
      Number(scrollThrottleMS)
    )
  )

  /**
   * @param {Logger} log
   * @param {string} position
   * @returns {Promise<void>}
   */
  function waitForSlotDefined(log, position) {
    return new Promise((resolve) => {
      log('waiting for slot defined')
      const sub = broker.on('slot defined', (slotDefinedEvent) => {
        if (slotDefinedEvent.pos === position) {
          sub.cancel()
          log('slot defined')
          resolve()
        }
      })
    })
  }

  /**
   * @param {Logger} log
   * @param {string} trigger
   * @returns {Promise<void>}
   */
  async function waitForTrigger(log, trigger) {
    log('waiting for trigger to appear')
    const predicate = () => !!doc.querySelector(trigger)
    if (!predicate()) {
      await repeatUntil(predicate)
    }
    log('trigger appeared')
  }

  /**
   * @param {string} position
   * @param {Logger} log
   * */
  function hidePos(position, log) {
    const disposable = new Disposable()
    const hideTime = Date.now()
    sendToBidmax({ type: 'hidden', position })
    disposable.add(() => {
      sendToBidmax({ type: 'show', value: Date.now() - hideTime, position })
    })
    disposable.add(broker.intercept('reset refresh info for pos', position, () => {
      log('intercepted (\'reset refresh info for pos\')', position)
    }))

    disposable.add(broker.intercept('slot mounted',
      // @ts-ignore Broker types don't except filters yet
      objectFilter({ pos: position }), () => {
        log('intercepted (\'slot mounted\')', position)
      }))

    disposable.add(broker.intercept('slot ready for ad',
      // @ts-ignore Broker types don't except filters yet
      objectFilter({ pos: position }), () => {
        log('intercepted (\'slot ready for ad\')', position)
        broker.emit('peekaboo hide', position)
      }))

    disposable.add(broker.intercept('request ad', ([, , , opts], next) => {
      if (opts.pos === position) {
        log('intercepted (\'request ad\')', position)
      } else {
        return next()
      }
    }))

    disposable.add(broker.intercept('slot ready to refresh', ([, arr], next) => {
      if (arr?.[0]?.ad?.pos === position) {
        log('intercepted (\'slot ready to refresh\')', position)
      } else {
        return next()
      }
    }))

    disposable.add(broker.intercept('slot ready to refresh slot', ([, , , opts], next) => {
      if (opts.pos === position) {
        log('intercepted (\'slot ready to refresh slot\')', position)
      } else {
        return next()
      }
    }))

    broker.emit('peekaboo hide', position)
    return disposable
  }

  /** @param {string} position */
  function showPos(position) {
    broker.emit('peekaboo show', position)
  }

  /**
   * @param {Record<string, any>} event
   */
  function sendToBidmax(event) {
    broker.emit('send event to bidmax', { type: 'custom', payload: { module: 'peekaboo', ...event } })
  }
}

/**
 * @typedef {Record<string, {
 *   recreateOnShow?: boolean,
 *   conditions: {
 *     slotEnabled?: string,
 *     pluginEnabled?: string
 *   },
 *   pauseOnOverlay?: boolean,
 *   triggers: string,
 *   position: string,
 *   offset: number,
 *   timeout?: number
 * }>} PeekabooMonitorConfig
 */
